import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSite } from "@/context/SiteContext";
import { PLANS } from "@/components/market/Plans";
import { fmt4, rateTime, usdNet } from "@/components/market/FxNote";

const Chart = ({ points }) => {
  const path = useMemo(() => {
    if (points.length < 2) return "";
    const rates = points.map((p) => p.rate);
    const lo = Math.min(...rates), hi = Math.max(...rates), span = hi - lo || 1e-6;
    return points.map((p, i) => `${i ? "L" : "M"}${(i / (points.length - 1)) * 100},${38 - ((p.rate - lo) / span) * 34}`).join(" ");
  }, [points]);
  if (!path) return null;
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="mt-3 h-20 w-full" aria-hidden data-testid="fx-chart">
      <path d={path} fill="none" stroke="var(--gold)" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const FxCard = () => {
  const { t, locale } = useI18n();
  const { refresh: refreshSite } = useSite();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/fx").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);
  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fx/refresh");
      toast[data.ok ? "success" : "error"](data.ok ? t("fx.admin.updated") : t("fx.admin.failed"));
      await load();
      refreshSite();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  if (!d) return null;
  const c = d.current;
  const hist = d.history.slice(-12).reverse();
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="fx-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><DollarSign className="h-4 w-4 text-[var(--gold)]" /> {t("fx.admin.title")}</h2>
        <button type="button" className="btn-gold btn-sm" onClick={retry} disabled={busy} data-testid="fx-refresh-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("fx.admin.refresh")}
        </button>
      </div>
      {c ? (
        <div className="mt-3 grid min-w-0 gap-3 sm:grid-cols-3">
          <p className="min-w-0"><span className="block text-xs text-neutral-400">{t("fx.admin.rate")}</span><bdi className="font-display text-2xl font-bold text-white" data-testid="fx-current-rate">1 € = {fmt4(c.rate, locale)} $</bdi></p>
          <p className="min-w-0"><span className="block text-xs text-neutral-400">{t("fx.admin.updatedAt")}</span><bdi className="text-sm text-neutral-100" data-testid="fx-updated-at">{rateTime(c.updated_at, locale)}</bdi>
            {c.stale && <span className="ms-2 rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] text-amber-200" data-testid="fx-stale-badge">{t("fx.stale")}</span>}</p>
          <p className="min-w-0"><span className="block text-xs text-neutral-400">{t("fx.admin.source")}</span><span className="break-all text-sm text-neutral-100" data-testid="fx-source">{c.source}</span></p>
        </div>
      ) : <p className="mt-3 text-sm text-amber-200" data-testid="fx-none">{t("fx.admin.none")}</p>}
      {c && (
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-300" data-testid="fx-plan-prices">
          {PLANS.map((p) => <bdi key={p.id}>{p.name} : {p.eur} € → {new Intl.NumberFormat(locale, { minimumFractionDigits: 2 }).format(usdNet(p.eur, c.rate))} $ HT</bdi>)}
        </p>
      )}
      {!d.last_ok && Object.keys(d.errors).length > 0 && <p className="mt-2 break-words text-xs text-red-300" data-testid="fx-errors">{t("fx.admin.failed")} {Object.keys(d.errors).join(", ")}</p>}
      <Chart points={d.history} />
      <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-neutral-400" data-testid="fx-history">
        {hist.map((h) => <li key={h.at} className="flex justify-between gap-3"><bdi>{rateTime(h.at, locale)}</bdi><bdi className="tabular-nums text-neutral-200">{fmt4(h.rate, locale)}</bdi><span className="truncate">{h.source}</span></li>)}
      </ul>
      <p className="mt-3 text-[0.7rem] text-neutral-500">{t("fx.admin.hint")}</p>
    </section>
  );
};
